import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faChevronRight, faCog } from '@fortawesome/free-solid-svg-icons';
import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { cleanPath } from '../../cleanPath';
import { IToolActivity } from '../../types';

interface IProps {
  activity: IToolActivity;
}

function getActivityLabel(activity: IToolActivity): string {
  const name = activity.toolName;
  const input = activity.input;

  if (name === 'Read' && input.file_path) {
    return 'Read ' + cleanPath(input.file_path);
  }
  if (name === 'Write' && input.file_path) {
    return 'Write ' + cleanPath(input.file_path);
  }
  if (name === 'Edit' && input.file_path) {
    return 'Edit ' + cleanPath(input.file_path);
  }
  if (name === 'Grep' && input.pattern) {
    return 'Grep "' + input.pattern + '"';
  }
  if (name === 'Glob' && input.pattern) {
    return 'Glob ' + cleanPath(input.pattern);
  }
  if (name === 'Bash' && input.command) {
    const cmd = cleanPath(String(input.command));
    return 'Bash ' + (cmd.length > 60 ? cmd.substring(0, 57) + '...' : cmd);
  }
  if (name.indexOf('mcp__') === 0) {
    const parts = name.split('__');
    return 'MCP ' + (parts[1] || '') + ' ' + (parts[2] || '');
  }
  return name;
}

const ToolActivity: React.FC<IProps> = ({ activity }) => {
  const [expanded, setExpanded] = useState(false);
  const isRunning = activity.status === 'running';
  const label = getActivityLabel(activity);

  return (
    <Container>
      <Header onClick={() => setExpanded(!expanded)}>
        <StatusIcon>
          {isRunning ? (
            <SpinningCog icon={faCog} />
          ) : (
            <FontAwesomeIcon icon={faCheck} style={{ color: '#4caf50' }} />
          )}
        </StatusIcon>
        <Label>{label}</Label>
        {isRunning && activity.elapsedSeconds != null && activity.elapsedSeconds > 0 && (
          <Elapsed>{Math.round(activity.elapsedSeconds)}s</Elapsed>
        )}
        <ExpandIcon icon={expanded ? faChevronDown : faChevronRight} />
      </Header>
      {expanded && (
        <Details>
          <DetailSection>
            <DetailLabel>Input:</DetailLabel>
            <DetailCode>{JSON.stringify(activity.input, (_, v) => typeof v === 'string' ? cleanPath(v) : v, 2)}</DetailCode>
          </DetailSection>
          {activity.summary && (
            <DetailSection>
              <DetailLabel>Result:</DetailLabel>
              <DetailText>{activity.summary}</DetailText>
            </DetailSection>
          )}
        </Details>
      )}
    </Container>
  );
};

export default ToolActivity;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  margin: 3px 0;
  border-left: 2px solid #e0e0e0;
  font-size: 12px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 3px 8px;
  cursor: pointer;
  border-radius: 3px;
  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const StatusIcon = styled.span`
  margin-right: 6px;
  font-size: 11px;
  width: 14px;
  text-align: center;
  color: #888;
`;

const SpinningCog = styled(FontAwesomeIcon)`
  animation: ${spin} 1.5s linear infinite;
`;

const Label = styled.span`
  flex: 1;
  color: #666;
  font-family: 'Courier New', monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Elapsed = styled.span`
  color: #999;
  margin-left: 8px;
  font-size: 11px;
`;

const ExpandIcon = styled(FontAwesomeIcon)`
  font-size: 9px;
  color: #bbb;
  margin-left: 6px;
`;

const Details = styled.div`
  padding: 4px 8px 4px 28px;
`;

const DetailSection = styled.div`
  margin-bottom: 4px;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #888;
  font-size: 11px;
`;

const DetailCode = styled.pre`
  background: #f0f0f0;
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 11px;
  overflow-x: auto;
  margin: 2px 0 0;
  max-height: 150px;
  overflow-y: auto;
`;

const DetailText = styled.span`
  color: #666;
  font-size: 11px;
`;
